import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { staticRoutesWithCustomSidebar } from 'Deer/constants/staticRoutesWithCustomSidebar';
import { useQuery } from 'components/hooks/useQuery';
import { useToast } from 'components/hooks/useToast';
import { useHistory } from 'react-router-dom';
import { CustomHeaderCell } from 'screens/Team/Rabbit/Goals/AgGridTreeView/components/CustomHeaderCell';
import { checkNewNavEnabled } from 'utils/helpers/featureFlags';

import { Archive, Clone1, OutlineEye, Plus } from 'assets/assetsV1/iconComponents';

import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';
import { routes } from 'constants/routes';

import { archiveTemplate, duplicateTemplate } from 'services/templates/review';

import { MenuItemComp } from 'uiToolkitV1/AgGridRowMenu/AgGridRowMenu';
import { AgGridSearchBar } from 'uiToolkitV1/AgGridSearchBar';
import { AgGridTable } from 'uiToolkitV1/AgGridWrapperV2';
import { Button } from 'uiToolkitV1/Button';
import { ChipSelect } from 'uiToolkitV1/ChipSelect';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { useTemplateCreationSidePanel } from '../ReviewTemplate/TemplateCreationSidepanel/TemplateCreationSidepanelContext';
import {
  useGetActiveTemplatesQuery,
  useGetArchivedTemplatesQuery,
  useGetSampleTemplatesQuery,
} from './Hooks/dataHooks';
import PlainText from './components/PlainText';
import { generateReviewTemplateListOptions } from './utils';

const ReviewTemplateListV1 = () => {
  const history = useHistory();
  const { data: activeTemplates = [] } = useGetActiveTemplatesQuery();
  const { data: archivedTemplates = [] } = useGetArchivedTemplatesQuery();
  const { data: sampleTemplates = [] } = useGetSampleTemplatesQuery();
  const [selectedTemplateId, setSelectedTemplateId] = useState('active');
  const { showSidepanel } = useTemplateCreationSidePanel();
  const { isCreateTemplatePanelOpen } = useQuery();

  const gridRef = useRef();
  const queryClient = useQueryClient();
  const toast = useToast();

  let rowData = [];
  if (selectedTemplateId === 'active') {
    rowData = activeTemplates?.data ?? [];
  } else if (selectedTemplateId === 'archived') {
    rowData = archivedTemplates?.data ?? [];
  } else if (selectedTemplateId === 'sample') {
    rowData = sampleTemplates?.data ?? [];
  }

  useEffect(() => {
    if (isCreateTemplatePanelOpen === 'true') {
      onCreateNewClickHandler();
    }
  }, [isCreateTemplatePanelOpen]);

  // create new template
  const onCreateNewClickHandler = () => {
    showSidepanel({
      onSuccess: ({ templateId }) => {
        const templateRoute = checkNewNavEnabled()
          ? staticRoutesWithCustomSidebar.reviewCycleTemplateCreation.replace(':templateId', templateId)
          : routes.template.review.index.replace(':templateId', templateId);
        history.push(templateRoute);
      },
    });
  };

  // on chip change active, sample, archived
  const onTemplateChipChange = (id) => {
    // this will happen when user clicks on already active chip, we ignore this cause atleast one chip must be selected as per out use case here
    if (id?.length === 0) return;
    setSelectedTemplateId(id[0]);
  };

  const refetchHandler = () => {
    queryClient.invalidateQueries([REACT_QUERY_KEYS.reviewTemplateList.archivedTemplates]);
    queryClient.invalidateQueries([REACT_QUERY_KEYS.reviewTemplateList.activeTemplates]);
  };

  const menuItems = useMemo(() => {
    // Define menu items that are always present
    const baseMenuItems = [
      {
        Component: ({ data }) => <MenuItemComp Icon={OutlineEye} label="View" />,
        action: ({ data }) => {
          showSidepanel({
            onSuccess: ({ templateId, goToPreview }) => {
              if (goToPreview) {
                const path = checkNewNavEnabled()
                  ? staticRoutesWithCustomSidebar.reviewCycleTemplatePreview.replace(':templateId', templateId)
                  : routes.template.review.preview.replace(':templateId', templateId);
                window.open(path, '_blank');
              } else {
                const templateRoute = checkNewNavEnabled()
                  ? staticRoutesWithCustomSidebar.reviewCycleTemplateCreation.replace(':templateId', templateId)
                  : routes.template.review.index.replace(':templateId', templateId);
                history.push(templateRoute);
              }
            },
            templateId: data.id,
          });
        },
      },
    ];

    let additionalMenuItems = [];

    if (selectedTemplateId === 'active') {
      additionalMenuItems = [
        {
          Component: ({ data }) => <MenuItemComp Icon={Archive} label="Archive" />,
          action: ({ data }) => {
            archiveTemplate({ templateId: data.id, isArchive: true })
              .then((res) => {
                refetchHandler();
                toast({
                  title: `Template archived successfully`,
                  status: 'success',
                });
              })
              .catch((err) => {
                toast({
                  title: err.message,
                  status: 'error',
                });
              });
          },
        },
        {
          Component: ({ data }) => <MenuItemComp Icon={Clone1} label="Duplicate" />,
          action: ({ data }) => {
            duplicateTemplate({ templateId: data.id })
              .then((res) => {
                const templateRoute = checkNewNavEnabled()
                  ? staticRoutesWithCustomSidebar.reviewCycleTemplateCreation.replace(':templateId', res.id)
                  : routes.template.review.index.replace(':templateId', res.id);
                history.push(templateRoute);
                toast({
                  title: `Template duplicated successfully`,
                  status: 'success',
                });
              })
              .catch((err) => {
                toast({
                  title: err.message,
                  status: 'error',
                });
              });
          },
        },
      ];
    } else if (selectedTemplateId === 'archived') {
      additionalMenuItems = [
        {
          Component: ({ data }) => <MenuItemComp Icon={Archive} label="Unarchive" />,
          action: ({ data }) => {
            archiveTemplate({ templateId: data.id, isArchive: false })
              .then((res) => {
                refetchHandler();
                toast({
                  title: `Template unarchived successfully`,
                  status: 'success',
                });
              })
              .catch((err) => {
                toast({
                  title: err.message,
                  status: 'error',
                });
              });
          },
        },
      ];
    }

    return [...baseMenuItems, ...additionalMenuItems];
  }, [selectedTemplateId]);

  const columnDefs = useMemo(
    () => [
      {
        flex: 1,
        headerName: 'Template Name',
        width: 500,
        cellRenderer: PlainText,
        valueGetter: ({ data }) => data?.name,
      },
      {
        sortable: true,
        headerName: 'Created By',
        width: 300,
        valueGetter: ({ data }) => data?.createdByEmployee,
      },
      {
        filter: 'agNumberColumnFilter',
        width: 200,
        headerName: 'Questions',
        valueGetter: ({ data }) => data?.questionCount,
      },
    ],
    [selectedTemplateId]
  );
  const defaultColDef = useMemo(
    () => ({
      headerComponentParams: {
        enableMenu: true,
        enableSorting: true,
      },
      menuTabs: ['filterMenuTab'],
      headerComponent: CustomHeaderCell,
      filter: 'agTextColumnFilter',
      cellStyle: { display: 'flex', alignItems: 'center' },
    }),
    []
  );

  const onRowClicked = useCallback(
    ({ data, event }) => {
      if (event.defaultPrevented) {
        return;
      }
      showSidepanel({
        onSuccess: ({ templateId, goToPreview }) => {
          if (goToPreview) {
            const path = staticRoutesWithCustomSidebar.reviewCycleTemplatePreview.replace(':templateId', templateId);
            window.open(path, '_blank');
          } else {
            const templateRoute = checkNewNavEnabled()
              ? staticRoutesWithCustomSidebar.reviewCycleTemplateCreation.replace(':templateId', templateId)
              : routes.template.review.index.replace(':templateId', templateId);
            history.push(templateRoute);
          }
        },
        templateId: data.id,
      });
    },
    [selectedTemplateId]
  );

  const onArchiveClick = useCallback(
    (data) => {
      archiveTemplate({ templateId: data.id, isArchive: true })
        .then((res) => {
          refetchHandler();
          toast({
            title: `Template archived successfully`,
            status: 'success',
          });
        })
        .catch((err) => {
          toast({
            title: err.message,
            status: 'error',
          });
        });
    },
    [selectedTemplateId]
  );

  const onDuplicateClick = useCallback(
    (data) => {
      duplicateTemplate({ templateId: data.id })
        .then((res) => {
          const templateRoute = checkNewNavEnabled()
            ? staticRoutesWithCustomSidebar.reviewCycleTemplateCreation.replace(':templateId', res.id)
            : routes.template.review.index.replace(':templateId', res.id);
          history.push(templateRoute);
          toast({
            title: `Template duplicated successfully`,
            status: 'success',
          });
        })
        .catch((err) => {
          toast({
            title: err.message,
            status: 'error',
          });
        });
    },
    [selectedTemplateId]
  );

  const onUnarchiveClick = useCallback(
    (data) => {
      archiveTemplate({ templateId: data.id, isArchive: false })
        .then((res) => {
          refetchHandler();
          toast({
            title: `Template unarchived successfully`,
            status: 'success',
          });
        })
        .catch((err) => {
          toast({
            title: err.message,
            status: 'error',
          });
        });
    },
    [selectedTemplateId]
  );

  const onViewClick = useCallback(
    (data) => {
      showSidepanel({
        onSuccess: ({ templateId, goToPreview }) => {
          if (goToPreview) {
            const path = checkNewNavEnabled()
              ? staticRoutesWithCustomSidebar.reviewCycleTemplatePreview.replace(':templateId', templateId)
              : routes.template.review.preview.replace(':templateId', templateId);
            window.open(path, '_blank');
          } else {
            const templateRoute = checkNewNavEnabled()
              ? staticRoutesWithCustomSidebar.reviewCycleTemplateCreation.replace(':templateId', templateId)
              : routes.template.review.index.replace(':templateId', templateId);
            history.push(templateRoute);
          }
        },
        templateId: data.id,
      });
    },
    [selectedTemplateId]
  );

  const generateMenuOptions = useCallback(
    (data) =>
      generateReviewTemplateListOptions({
        data,
        onArchiveClick,
        onDuplicateClick,
        onUnarchiveClick,
        onViewClick,
        selectedTemplateId,
      }),
    [selectedTemplateId, rowData, onArchiveClick, onDuplicateClick, onViewClick]
  );

  const sideBar = useMemo(
    () => ({
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true,
            suppressColumnFilter: false,
            suppressColumnSelectAll: true,
            suppressColumnExpandAll: true,
          },
        },
      ],
      defaultToolPanel: '',
    }),
    []
  );

  return (
    <Box
      p={checkNewNavEnabled() ? 3 : '22px 24px'}
      m={checkNewNavEnabled() ? 0 : 4}
      borderRadius="8px"
      bgColor="common.white"
    >
      <Flex mb="12px" justifyContent="space-between">
        <AgGridSearchBar gridRef={gridRef} />
        <Button StartIcon={Plus} onClick={onCreateNewClickHandler}>
          Create New Template
        </Button>
      </Flex>
      <Flex alignItems="center" gap={2} mb={5}>
        <TextBodyMedium color="text.g3" bold>
          Showing
        </TextBodyMedium>
        <ChipSelect
          options={[
            { id: 'active', text: `Active` },
            { id: 'archived', text: `Archived` },
            { id: 'sample', text: `Sample` },
          ]}
          selectedIds={[selectedTemplateId]}
          onChange={onTemplateChipChange}
        />
      </Flex>

      <AgGridTable
        // REFER: https://www.reddit.com/r/reactjs/comments/q600rw/comment/hg8ygq5/?utm_source=share&utm_medium=web3x&utm_name=web3xcss&utm_term=1&utm_content=share_button
        key={selectedTemplateId}
        ref={gridRef}
        style={{ width: '100%', height: '100%' }}
        rowHeight={70}
        domLayout="autoHeight"
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        rowData={rowData}
        pagination
        rowStyle={{ cursor: 'pointer' }}
        paginationPageSize={10}
        cacheQuickFilter
        onRowClicked={onRowClicked}
        sideBar={sideBar}
        kebabMenuIsRowSpecific
        kebabMenuOptionsGenerator={generateMenuOptions}
        containerAutoHeight
      />
    </Box>
  );
};

export default ReviewTemplateListV1;
